import {
    ActionContext,
    ActionTree,
    GetterTree,
    MutationTree,
    Module,
    Store as VuexStore,
    CommitOptions,
    DispatchOptions,
} from 'vuex'

import {State as RootState} from '@/store'
import {FaceGroupingJob, FaceGroupingStatus, FacePerson, FacePersonPhoto} from "@/types/FaceGroupingType";
import {
    deleteFaceGrouping,
    getFaceGroupingStatus,
    getPeople,
    getPersonPhotos,
    startFaceGrouping,
} from "@/data/requests/faceGrouping";
import {ResponseError} from "@/types";

export type State = {
    job: FaceGroupingJob | null,
    status: FaceGroupingStatus,
    people: FacePerson[],
    personPhotos: FacePersonPhoto[],
    loading: boolean,
};

export type Mutations<S = State> = {
    setJob(state: S, {job}: {job: FaceGroupingJob | null}): void,
    setStatus(state: S, {status}: {status: FaceGroupingStatus}): void,
    setPeople(state: S, {people}: {people: FacePerson[]}): void,
    setPersonPhotos(state: S, {photos}: {photos: FacePersonPhoto[]}): void,
    setLoading(state: S, {loading}: {loading: boolean}): void,
    reset(state: S): void,
};

type AugmentedActionContext = {
    commit<K extends keyof Mutations>(
        key: K,
        payload: Parameters<Mutations[K]>[1],
    ): ReturnType<Mutations[K]>
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
    start(
        {commit, dispatch}: AugmentedActionContext,
        payload: {gallery: string, success?: () => void, failed?: (type: ResponseError, data: any) => void},
    ): void;

    loadStatus(
        {commit}: AugmentedActionContext,
        payload: {gallery: string},
    ): void;

    loadPeople(
        {commit}: AugmentedActionContext,
        payload: {gallery: string},
    ): void;

    loadPersonPhotos(
        {commit}: AugmentedActionContext,
        payload: {gallery: string, cluster: number | string},
    ): void;

    remove(
        {commit}: AugmentedActionContext,
        payload: {gallery: string, success?: () => void},
    ): void;

    reset({commit}: AugmentedActionContext): void;
}

export type Getters = {
    job(state: State): FaceGroupingJob | null;
    status(state: State): FaceGroupingStatus;
    people(state: State): FacePerson[];
    personPhotos(state: State): FacePersonPhoto[];
    loading(state: State): boolean;
    progress(state: State): number;
};

const state: State = {
    job: null,
    status: 'none',
    people: [],
    personPhotos: [],
    loading: false,
};

const getters: GetterTree<State, RootState> & Getters = {
    job: (state) => state.job,
    status: (state) => state.status,
    people: (state) => state.people,
    personPhotos: (state) => state.personPhotos,
    loading: (state) => state.loading,
    progress: (state) => {
        if (!state.job || state.job.total_photos === 0) return 0;
        return Math.round((state.job.processed_photos / state.job.total_photos) * 100);
    },
};

const mutations: MutationTree<State> & Mutations = {
    setJob(state, {job}) {
        state.job = job;
    },
    setStatus(state, {status}) {
        state.status = status;
    },
    setPeople(state, {people}) {
        state.people = people;
    },
    setPersonPhotos(state, {photos}) {
        state.personPhotos = photos;
    },
    setLoading(state, {loading}) {
        state.loading = loading;
    },
    reset(state) {
        state.job = null;
        state.status = 'none';
        state.people = [];
        state.personPhotos = [];
        state.loading = false;
    },
};

const actions: ActionTree<State, RootState> & Actions = {
    start({commit, dispatch}, {gallery, success, failed}) {
        commit('setLoading', {loading: true});
        startFaceGrouping(gallery, (job) => {
            commit('setJob', {job});
            commit('setStatus', {status: job.status});
            commit('setLoading', {loading: false});
            success?.();
        }, (type, data) => {
            commit('setLoading', {loading: false});
            failed?.(type, data);
        });
    },

    loadStatus({commit}, {gallery}) {
        getFaceGroupingStatus(gallery, (data) => {
            if ((data as any).status === 'none') {
                commit('setStatus', {status: 'none'});
                commit('setJob', {job: null});
                return;
            }
            const job = data as FaceGroupingJob;
            commit('setJob', {job});
            commit('setStatus', {status: job.status});
        });
    },

    loadPeople({commit}, {gallery}) {
        commit('setLoading', {loading: true});
        getPeople(gallery, (people) => {
            commit('setPeople', {people});
            commit('setLoading', {loading: false});
        }, () => {
            commit('setLoading', {loading: false});
        });
    },

    loadPersonPhotos({commit}, {gallery, cluster}) {
        commit('setLoading', {loading: true});
        getPersonPhotos(gallery, cluster, (photos) => {
            commit('setPersonPhotos', {photos});
            commit('setLoading', {loading: false});
        }, () => {
            commit('setLoading', {loading: false});
        });
    },

    remove({commit}, {gallery, success}) {
        deleteFaceGrouping(gallery, () => {
            commit('reset', undefined);
            success?.();
        });
    },

    reset({commit}) {
        commit('reset', undefined);
    },
};

export type Store<S = State> = Omit<
    VuexStore<S>,
    'commit' | 'getters' | 'dispatch'
> & {
    commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
        key: K,
        payload: P,
        options?: CommitOptions,
    ): ReturnType<Mutations[K]>
} & {
    getters: {
        [K in keyof Getters]: ReturnType<Getters[K]>
    }
} & {
    dispatch<K extends keyof Actions>(
        key: K,
        payload: Parameters<Actions[K]>[1],
        options?: DispatchOptions,
    ): ReturnType<Actions[K]>
}

export const FaceGroupingModule: Module<State, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
